import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Heart, HeartOff } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

interface Props {
  ideaId: string;
  ownerId: string;
}

export default function FollowIdeaButton({ ideaId, ownerId }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const { data: countData } = useQuery({
    queryKey: ["idea-followers-count", ideaId],
    queryFn: async () => {
      const { count } = await supabase
        .from("idea_followers")
        .select("id", { count: "exact", head: true })
        .eq("idea_id", ideaId);
      return count ?? 0;
    },
  });

  const { data: isFollowing } = useQuery({
    queryKey: ["idea-is-following", ideaId, user?.id],
    queryFn: async () => {
      if (!user) return false;
      const { data } = await supabase
        .from("idea_followers")
        .select("id")
        .eq("idea_id", ideaId)
        .eq("user_id", user.id)
        .maybeSingle();
      return !!data;
    },
    enabled: !!user,
  });

  if (user?.id === ownerId) return null;

  const toggle = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setBusy(true);
    if (isFollowing) {
      const { error } = await supabase
        .from("idea_followers")
        .delete()
        .eq("idea_id", ideaId)
        .eq("user_id", user.id);
      if (error) toast.error("Could not unfollow");
      else toast.success("Unfollowed");
    } else {
      const { error } = await supabase
        .from("idea_followers")
        .insert({ idea_id: ideaId, user_id: user.id });
      if (error) toast.error("Could not follow");
      else toast.success("You'll get notified about updates");
    }
    setBusy(false);
    qc.invalidateQueries({ queryKey: ["idea-followers-count", ideaId] });
    qc.invalidateQueries({ queryKey: ["idea-is-following", ideaId, user.id] });
  };

  return (
    <Button
      variant={isFollowing ? "outline" : "default"}
      size="sm"
      onClick={toggle}
      disabled={busy}
      className="gap-2"
    >
      {isFollowing ? <HeartOff className="h-3.5 w-3.5" /> : <Heart className="h-3.5 w-3.5" />}
      {isFollowing ? "Following" : "Follow"}
      {typeof countData === "number" && countData > 0 && (
        <span className="text-xs opacity-70">· {countData}</span>
      )}
    </Button>
  );
}